/**
 * Themed two-click date-range calendar for the lab-results trend filter.
 *
 * Matches the medonc dashboard calendar's look (black selected day, today dot,
 * larger cells, clickable month/year dropdowns) but adds what that component
 * lacks: a selectable window (`minDate`/`maxDate`) so days without data are
 * greyed and unclickable. Unlike the medonc component it never re-emits its
 * range on mount — it only calls `onDateRangeChange` once the user completes a
 * start→end selection.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./DateRangeCalendar.scss";

/**
 * Props for {@link DateRangeCalendar}.
 */
interface DateRangeCalendarProps {
  /** Called with the inclusive [start, end] once a full range is selected. */
  onDateRangeChange?: (startDate: Date | null, endDate: Date | null) => void;
  /** Pre-selected range start (also seeds the visible month). */
  initialStartDate?: Date;
  /** Pre-selected range end. */
  initialEndDate?: Date;
  /** Earliest selectable day; earlier days are greyed and unclickable. */
  minDate?: Date;
  /** Latest selectable day; later days are greyed and unclickable. */
  maxDate?: Date;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Strip a date down to local midnight for day-granular comparisons.
 *
 * @param date - Any date.
 * @returns A new date at 00:00 local time on the same calendar day.
 */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Whole-month index (year * 12 + month) for month-level comparisons.
 *
 * @param date - Any date.
 * @returns The month index.
 */
function monthIndex(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

/**
 * Render an interactive date-range calendar constrained to a selectable window.
 *
 * @param props - Initial range, selectable bounds, and the selection callback.
 * @returns The calendar UI.
 */
export function DateRangeCalendar(props: DateRangeCalendarProps) {
  const { onDateRangeChange, initialStartDate, initialEndDate, minDate, maxDate } = props;

  const [currentMonth, setCurrentMonth] = useState(
    () => initialStartDate || minDate || new Date(),
  );
  const [startDate, setStartDate] = useState<Date | null>(() => initialStartDate || null);
  const [endDate, setEndDate] = useState<Date | null>(() => initialEndDate || null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Close both month/year dropdowns.
   *
   * @returns Nothing.
   */
  const closeSelectors = useCallback(() => {
    setShowMonthSelector(false);
    setShowYearSelector(false);
  }, []);

  // Dismiss the month/year dropdowns on Escape or a click outside the calendar,
  // matching the other popovers in this dashboard so an open list never lingers
  // over the day grid.
  useEffect(() => {
    if (!showMonthSelector && !showYearSelector) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSelectors();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeSelectors();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [showMonthSelector, showYearSelector, closeSelectors]);

  // Re-seed when the caller supplies a new range (e.g. switching lab markers or
  // resetting). Never fires the change callback — that only happens on a click.
  useEffect(() => {
    setStartDate(initialStartDate || null);
    setEndDate(initialEndDate || null);
    if (initialStartDate) setCurrentMonth(initialStartDate);
  }, [initialStartDate, initialEndDate]);

  const isDisabled = useCallback(
    (date: Date): boolean => {
      const day = startOfDay(date);
      if (minDate && day < startOfDay(minDate)) return true;
      if (maxDate && day > startOfDay(maxDate)) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const handlePrevMonth = useCallback(() => {
    setShowMonthSelector(false);
    setShowYearSelector(false);
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setShowMonthSelector(false);
    setShowYearSelector(false);
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1));
  }, []);

  const handleMonthTitleClick = useCallback(() => {
    setShowMonthSelector((open) => !open);
    setShowYearSelector(false);
  }, []);

  const handleYearTitleClick = useCallback(() => {
    setShowYearSelector((open) => !open);
    setShowMonthSelector(false);
  }, []);

  const handleMonthSelect = useCallback((month: number) => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), month));
    setShowMonthSelector(false);
  }, []);

  const handleYearSelect = useCallback(
    (year: number) => {
      // Keep the viewed month but clamp into the selectable window, so picking a
      // year can never land on a fully-out-of-range month the arrows would block.
      setCurrentMonth((prev) => {
        const target = new Date(year, prev.getMonth());
        if (minDate && monthIndex(target) < monthIndex(minDate)) {
          return new Date(minDate.getFullYear(), minDate.getMonth());
        }
        if (maxDate && monthIndex(target) > monthIndex(maxDate)) {
          return new Date(maxDate.getFullYear(), maxDate.getMonth());
        }
        return target;
      });
      setShowYearSelector(false);
    },
    [minDate, maxDate],
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      if (isDisabled(date)) return;
      // A day click also dismisses any open month/year dropdown so the picked
      // range is never hidden underneath it.
      closeSelectors();
      // First click (or a click after a completed range) begins a new selection.
      if (!startDate || endDate) {
        setStartDate(date);
        setEndDate(null);
        return;
      }
      // Second click completes the range in either direction: clicking an
      // earlier day than the anchor selects [clicked, anchor] rather than
      // resetting the start, so picking a recent day then going back works.
      const start = date < startDate ? date : startDate;
      const end = date < startDate ? startDate : date;
      setStartDate(start);
      setEndDate(end);
      onDateRangeChange?.(start, end);
    },
    [startDate, endDate, isDisabled, onDateRangeChange, closeSelectors],
  );

  const handleDateHover = useCallback(
    (date: Date) => {
      if (startDate && !endDate && !isDisabled(date)) setHoverDate(date);
    },
    [startDate, endDate, isDisabled],
  );

  const days = useMemo((): (Date | null)[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const cells: (Date | null)[] = Array(firstDayOfMonth).fill(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(year, month, day));
    }
    return cells;
  }, [currentMonth]);

  // Years offered in the dropdown: constrained to the data window when bounds
  // exist, otherwise a ±10-year span around the current year.
  const years = useMemo((): number[] => {
    const fallback = new Date().getFullYear();
    const first = minDate ? minDate.getFullYear() : fallback - 10;
    const last = maxDate ? maxDate.getFullYear() : fallback + 10;
    const list: number[] = [];
    for (let year = first; year <= last; year += 1) list.push(year);
    return list;
  }, [minDate, maxDate]);

  /**
   * Whether an entire month lies outside the selectable window (for the year
   * currently in view), so the month option can be greyed in the dropdown.
   */
  const isMonthDisabled = useCallback(
    (month: number): boolean => {
      const year = currentMonth.getFullYear();
      const firstOfMonth = new Date(year, month, 1);
      const lastOfMonth = new Date(year, month + 1, 0);
      if (minDate && lastOfMonth < startOfDay(minDate)) return true;
      if (maxDate && firstOfMonth > startOfDay(maxDate)) return true;
      return false;
    },
    [currentMonth, minDate, maxDate],
  );

  const isInRange = useCallback(
    (date: Date): boolean => {
      if (!startDate) return false;
      const day = startOfDay(date);
      if (endDate) return day >= startOfDay(startDate) && day <= startOfDay(endDate);
      if (hoverDate) {
        const from = startOfDay(startDate);
        const to = startOfDay(hoverDate);
        return day >= (from < to ? from : to) && day <= (from < to ? to : from);
      }
      return false;
    },
    [startDate, endDate, hoverDate],
  );

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const cellClassName = (date: Date | null): string => {
    if (!date) return "drc-cell";
    const classes = ["drc-cell", "has-date"];
    if (isDisabled(date)) classes.push("disabled");
    if (startDate && startOfDay(date).getTime() === startOfDay(startDate).getTime()) classes.push("start-date");
    if (endDate && startOfDay(date).getTime() === startOfDay(endDate).getTime()) classes.push("end-date");
    if (isInRange(date)) classes.push("in-range");
    if (isToday(date)) classes.push("today");
    return classes.join(" ");
  };

  const canGoPrev = minDate ? monthIndex(currentMonth) > monthIndex(minDate) : true;
  const canGoNext = maxDate ? monthIndex(currentMonth) < monthIndex(maxDate) : true;

  return (
    <div ref={containerRef} className="date-range-calendar" onMouseLeave={() => setHoverDate(null)}>
      <div className="drc-month-header">
        <button
          type="button"
          className="drc-nav-button"
          onClick={handlePrevMonth}
          disabled={!canGoPrev}
          aria-label="Previous month"
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </button>
        <div className="drc-month-year">
          <button type="button" className="drc-title-button" onClick={handleMonthTitleClick}>
            {MONTHS[currentMonth.getMonth()]}
          </button>
          <button type="button" className="drc-title-button" onClick={handleYearTitleClick}>
            {currentMonth.getFullYear()}
          </button>
          {showMonthSelector && (
            <div className="drc-selector-dropdown drc-month-dropdown">
              {MONTHS.map((month, index) => (
                <button
                  key={month}
                  type="button"
                  className={`drc-selector-item${
                    index === currentMonth.getMonth() ? " selected" : ""
                  }`}
                  disabled={isMonthDisabled(index)}
                  onClick={() => handleMonthSelect(index)}
                >
                  {month}
                </button>
              ))}
            </div>
          )}
          {showYearSelector && (
            <div className="drc-selector-dropdown drc-year-dropdown">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`drc-selector-item${
                    year === currentMonth.getFullYear() ? " selected" : ""
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="drc-nav-button"
          onClick={handleNextMonth}
          disabled={!canGoNext}
          aria-label="Next month"
        >
          <ChevronRight size={20} strokeWidth={2} />
        </button>
      </div>
      <div className="drc-grid">
        <div className="drc-weekdays">
          {WEEKDAYS.map((day) => (
            <div key={day} className="drc-weekday-cell">
              {day.substring(0, 2)}
            </div>
          ))}
        </div>
        <div className="drc-days">
          {days.map((date, index) => (
            <div
              key={index}
              className={cellClassName(date)}
              onClick={() => date && handleDateClick(date)}
              onMouseEnter={() => date && handleDateHover(date)}
            >
              <span>{date ? date.getDate() : "\u00A0"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DateRangeCalendar;
